if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this._cache = null;       // { url, html }
        this._prefetchPromise = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        if (this._prefetchReady) return;
        this._prefetchReady = true;
        super.connectedCallback?.();
        // After element moves to body, wire up hover prefetch on the opener
        requestAnimationFrame(() => {
          const opener = document.querySelector(`[data-modal="#${this.id}"]`);
          if (!opener) return;
          const btn = opener.querySelector('[data-product-url]');
          if (!btn) return;
          const url = btn.getAttribute('data-product-url');
          opener.addEventListener('mouseenter', () => this._prefetch(url), { once: true });
          opener.addEventListener('focusin', () => this._prefetch(url), { once: true });
        });
      }

      _prefetch(url) {
        if (!url || this._cache?.url === url || this._prefetchPromise) return;
        this._prefetchPromise = fetch(url)
          .then((r) => r.text())
          .then((html) => { this._cache = { url, html }; })
          .catch(() => {})
          .finally(() => { this._prefetchPromise = null; });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        const url = opener.getAttribute('data-product-url');
        const done = () => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading__spinner').classList.add('hidden');
        };

        const render = (responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector('product-info');
          this.preprocessHTML(productElement);
          HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
          super.show(opener);
          done();
          this._wireThumbnailTouch();
        };

        // Instant render from cache
        if (this._cache?.url === url) {
          render(this._cache.html);
          return;
        }

        // Prefetch already in flight — wait for it instead of firing a second request
        if (this._prefetchPromise) {
          this._prefetchPromise.then(() => {
            if (this._cache?.url === url) {
              render(this._cache.html);
            } else {
              fetch(url).then((r) => r.text()).then(render).catch(done);
            }
          });
          return;
        }

        // No cache, no prefetch — fetch now
        fetch(url)
          .then((r) => r.text())
          .then((html) => {
            this._cache = { url, html };
            render(html);
          })
          .catch(done);
      }

      // Injects prev/next arrows on main image carousel.
      // Uses gallery.setActiveMedia() for navigation (correct sync with variants).
      // Only operates on "real" items (clientWidth > 0, laid out in flex).
      _wireThumbnailTouch() {
        requestAnimationFrame(() => {
          const gallery = this.modalContent.querySelector('media-gallery');
          if (!gallery) return;

          const mediaList = gallery.querySelector('.product__media-list');
          if (!mediaList) return;

          mediaList.style.position = 'relative';

          const caretSVG = () => `<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>`;

          // Source of truth: thumbnail list items (always 1:1 with real images, survive variant changes)
          const getThumbItems = () =>
            Array.from(gallery.elements?.thumbnails?.querySelectorAll('[data-target]') || []);

          // Active thumbnail = the one with aria-current="true"
          const getActiveThumb = (thumbs) =>
            thumbs.find((t) => t.querySelector('button')?.getAttribute('aria-current') === 'true') || thumbs[0];

          // Navigate using thumbnail targets → setActiveMedia keeps everything in sync
          const navigate = (delta) => {
            const thumbs = getThumbItems();
            if (!thumbs.length) return;
            const active = getActiveThumb(thumbs);
            const idx = thumbs.indexOf(active);
            const target = thumbs[idx + delta];
            if (target) gallery.setActiveMedia?.(target.dataset.target, false);
          };

          // ── Arrows ────────────────────────────────────────────────
          const prevBtn = document.createElement('button');
          prevBtn.className = 'tsf-carousel-btn tsf-carousel-btn--prev tsf-hidden';
          prevBtn.setAttribute('aria-label', 'Previous image');
          prevBtn.innerHTML = caretSVG();
          prevBtn.addEventListener('click', () => navigate(-1));

          const nextBtn = document.createElement('button');
          nextBtn.className = 'tsf-carousel-btn tsf-carousel-btn--next';
          nextBtn.setAttribute('aria-label', 'Next image');
          nextBtn.innerHTML = caretSVG();
          nextBtn.addEventListener('click', () => navigate(+1));

          mediaList.appendChild(prevBtn);
          mediaList.appendChild(nextBtn);

          // ── Sync arrow visibility ──────────────────────────────────
          const syncArrows = () => {
            const thumbs = getThumbItems();
            if (!thumbs.length) {
              prevBtn.classList.add('tsf-hidden');
              nextBtn.classList.add('tsf-hidden');
              return;
            }
            const active = getActiveThumb(thumbs);
            const idx = thumbs.indexOf(active);
            prevBtn.classList.toggle('tsf-hidden', idx <= 0);
            nextBtn.classList.toggle('tsf-hidden', idx >= thumbs.length - 1 || thumbs.length <= 1);
          };

          // Sync when aria-current changes (thumbnail click, variant change, arrow click)
          new MutationObserver(syncArrows).observe(
            gallery.elements?.thumbnails || gallery,
            { subtree: true, attributeFilter: ['aria-current'] }
          );

          // Also sync on scroll (user swipe)
          let scrollTimer;
          mediaList.addEventListener('scroll', () => {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(syncArrows, 120);
          }, { passive: true });

          // Run twice — first call may precede gallery's own aria-current assignment
          syncArrows();
          setTimeout(syncArrows, 300);

          // Touchend fallback for thumbnail taps on mobile
          gallery.querySelectorAll('[data-target]').forEach((item) => {
            const btn = item.querySelector('button');
            if (!btn) return;
            btn.addEventListener('touchend', (e) => {
              e.preventDefault();
              gallery.setActiveMedia?.(item.dataset.target, false);
            }, { passive: false });
          });
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });
        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;
        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
